import React, { useState } from 'react';
import { ChatBubbleLeftRightIcon, PlayCircleIcon } from '@heroicons/react/24/outline';

import VideoEmbed from '../UseCaseComponents/VideoEmbed';
import ChatEmbed from '../UseCaseComponents/ChatEmbed';
import TextSection from '../UseCaseComponents/TextSection';
import RFPResponseOnePager from '../UseCaseComponents/RFPResponseOnePager';

// Registry of one-off "rich content" components -- same pattern as
// VideoReportStarterLayout's richContentComponents map, keyed by name so
// useCases.js can reference one by string.
const richContentComponents = {
  RFPResponseOnePager,
};

// "Try asking" / "Follow-up questions" suggestions shown next to the chat.
// Each is sent verbatim via the live chat instance's own sendPrompt(text)
// method when clicked (see the button below) -- no separate short/display
// version, what's shown is exactly what gets sent.
//
// Split into two groups: the opening prompt that kicks off a full BidFlow
// run (the thing to click first, on a fresh chat), and shorter follow-ups
// that only make sense once that run has actually produced something to
// ask about.
const STARTER_PROMPT =
  "Using all the documents attached in the RFP input space attached to this agent, generate a fully filled-out bid sheet based on the input template. Start by classifying the source files (bid template, SAP cost data, constraint files, etc.), confirm the scope (item, count, price tiers, round number, which lots we're bidding), and then walk through your cost waterfall methodology with a sample item before generating the full output. Populate every row, column, and tab from the original template exactly, with no items dropped or structure changed, and flag anything you had to estimate. Use all the rules laid out in the agent, don't use submission files to draw your calculations. Generate a downloadable Excel with an extra tab that lists out all the assumptions made in the process for human review.";

const FOLLOW_UP_PROMPTS = [
  'Which line items are closest to the margin floor?',
  'Walk me through the cost waterfall for a specific item.',
  'Flag anything that needs manual review before I submit.',
];

// Shared button for both the "Try asking" and "Follow-up questions" lists
// -- calls the live chat instance's sendPrompt(text) on click, disabled
// (with an explanatory tooltip) until that instance is ready.
const PromptButton = ({ prompt, onRun, ready }) => (
  <button
    type="button"
    onClick={() => onRun(prompt)}
    disabled={!ready}
    title={ready ? 'Run this in the chat' : 'Chat is still loading -- one second'}
    className="group flex w-full items-start gap-2 rounded-md bg-primary-50 px-3 py-2 text-left hover:bg-primary-100 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-primary-50"
  >
    <PlayCircleIcon
      className="mt-0.5 h-4 w-4 shrink-0 text-primary-600 group-hover:text-primary-700 group-disabled:text-gray-400"
      aria-hidden="true"
    />
    <span>"{prompt}"</span>
  </button>
);

// For a use case that's a recorded walkthrough video PLUS a live chat agent
// that visitors can actually try -- video up top (full width, same as
// VideoReportStarterLayout), then a two-column row with a short intro on
// the left and the live chat window on the right (chat stays pinned in
// view while the page scrolls, since it's the thing visitors interact
// with), then either the plain text explanation or a richContent one-pager
// underneath with the fuller writeup, full width.
//
// components shape:
//   video:       { url }              -- required for the video to show
//   text:        { heading, body }    -- optional intro copy (skipped if richContent is set)
//   richContent: 'ComponentName'      -- optional one-pager instead of the plain text block
//   chat:        { agentId }          -- required for the chat window to
//                                         show. There's no supported way to
//                                         pre-load a message into the
//                                         QuickSight chat embed without it
//                                         actually sending on page load
//                                         (see ChatEmbed), so instead the
//                                         "Try asking" list below sends a
//                                         suggestion through the live
//                                         chat instance's own sendPrompt()
//                                         method when a visitor clicks it.
const VideoChatStarterLayout = ({ useCase }) => {
  const { video, text, chat, richContent } = useCase.components;
  const RichContent = richContent ? richContentComponents[richContent] : null;
  const [isChatLoading, setIsChatLoading] = useState(true);
  const [chatInstance, setChatInstance] = useState(null);

  const runPrompt = (prompt) => {
    chatInstance?.sendPrompt(prompt);
  };

  return (
    <div className="mt-8 w-full">
      {/* When heroBanner is on (see useCases.js / UseCaseView), the video
          already shows up top on the gradient hero band -- rendering it
          again here would just duplicate it. Same pattern as
          VideoReportStarterLayout skipping its own video for a
          richContent use case. */}
      {!useCase.heroBanner && (
        <VideoEmbed
          url={video?.url}
          title={useCase.title}
          className="w-full rounded-xl"
        />
      )}

      {/* items-stretch (was here before) forces both columns to match the
          taller one's height -- since the left "Try it yourself" panel's
          text + prompt lists are naturally taller than the chat needs, it
          was stretching the chat's card to match regardless of any height
          set on the chat itself. That's why lowering the chat's own
          height never visibly changed anything: the empty space just
          moved from below the chat's input box to inside the card below
          it. items-start lets each column size to its own content
          instead. */}
      {chat?.agentId && (
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[7fr_13fr] lg:items-start">
          {/* Short intro next to the chat instead of the chat sitting alone
              -- pulls from `about` (already written as a one-line pitch for
              the use case card) rather than duplicating the full one-pager
              copy that follows below. Filled with sample prompts (pulled
              from the BidFlow walkthrough video) so it's not just a mostly-
              empty colored block. h-full/flex-col no longer stretch this
              to match the chat's height (the grid is items-start now, not
              items-stretch -- see the grid div's comment above), so this
              sizes to its own content and the chat next to it sizes to
              its own, independently. Custom 7fr/13fr template (35%/65%,
              was 1/4 vs 3/4) so this box has more room to breathe now that
              its "Try asking" suggestions are full sentences, not
              one-liners. */}
          <div className="order-2 flex h-full flex-col rounded-xl border border-primary-200 bg-primary-50 p-6 shadow-sm lg:order-1">
            <h3 className="text-xl font-semibold text-primary-900">
              Try it yourself
            </h3>
            <p className="mt-3 text-sm text-gray-700">
              {useCase.about}
            </p>
            <p className="mt-3 text-sm text-gray-700">
              Click a suggestion below to run it, or ask your own question on the right -- it's the same live agent shown in the walkthrough above.
            </p>

            <div className="mt-6 rounded-lg border border-primary-200 bg-white p-4">
              <h4 className="text-sm font-semibold uppercase tracking-wide text-primary-700">
                Try asking
              </h4>
              <p className="mt-1 text-xs text-gray-500">
                Start here on a fresh chat
              </p>
              <ul className="mt-3 space-y-2 text-sm text-gray-700">
                <li>
                  <PromptButton
                    prompt={STARTER_PROMPT}
                    onRun={runPrompt}
                    ready={!!chatInstance}
                  />
                </li>
              </ul>
            </div>

            <div className="mt-4 rounded-lg border border-primary-200 bg-white p-4">
              <h4 className="text-sm font-semibold uppercase tracking-wide text-primary-700">
                Follow-up questions
              </h4>
              <p className="mt-1 text-xs text-gray-500">
                Ask these once the bid sheet above has actually run.
              </p>
              <ul className="mt-3 space-y-2 text-sm text-gray-700">
                {FOLLOW_UP_PROMPTS.map((prompt) => (
                  <li key={prompt}>
                    <PromptButton
                      prompt={prompt}
                      onRun={runPrompt}
                      ready={!!chatInstance}
                    />
                  </li>
                ))}
              </ul>
            </div>

            <p className="mt-auto pt-6 text-xs text-gray-500">
              Every response traces back to the source cost, freight, and template files -- nothing is calculated from a file that wasn't explicitly authorized for the round.
            </p>
          </div>

          <div className="order-1 w-full overflow-hidden rounded-2xl border-2 border-primary-800 shadow-lg lg:order-2 lg:sticky lg:top-8">
            <div className="flex items-center gap-2 bg-primary-800 px-3 py-3 sm:gap-3 sm:px-5">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/10 sm:h-8 sm:w-8">
                <ChatBubbleLeftRightIcon className="h-4 w-4 text-white sm:h-5 sm:w-5" aria-hidden="true" />
              </span>
              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-white sm:text-base">
                West Loop Strategy Assistant
              </span>
              <span className="ml-auto flex shrink-0 items-center gap-1.5 whitespace-nowrap text-xs font-medium text-white/70">
                <span
                  className={`h-2 w-2 shrink-0 rounded-full ${
                    isChatLoading ? 'animate-pulse bg-yellow-400' : 'bg-green-400'
                  }`}
                  aria-hidden="true"
                />
                {isChatLoading ? 'Connecting...' : 'Online'}
              </span>
            </div>

            <div className="bg-white">
              {/* Was a dvh-based clamp() that grew with the viewport --
                  even scaled down twice, it kept landing taller than the
                  chat actually needs and leaving empty space below the
                  last message. Fixed height instead (a smaller one below
                  the sm breakpoint, since a flat 550px would be too tall
                  relative to a phone screen): this is roughly what a
                  default-size laptop browser window shows, so it no
                  longer grows just because the window/screen is taller. */}
              <ChatEmbed
                agentId={chat.agentId}
                className="h-[420px] sm:h-[550px] w-full"
                onLoadingChange={setIsChatLoading}
                onChatReady={setChatInstance}
              />
            </div>
          </div>
        </div>
      )}

      {!RichContent && (text?.heading || text?.body) && (
        <TextSection
          heading={text?.heading}
          body={text?.body}
          className="mt-8 w-full"
        />
      )}

      {RichContent && (
        <div className="mt-8 w-full">
          <RichContent />
        </div>
      )}
    </div>
  );
};

export default VideoChatStarterLayout;
